import { useCallback, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { ApiFieldError } from "@/shared/types/apiError";
import {
  FieldErrorContext,
  type FieldErrorContextValue,
} from "@/shared/contexts/field-error-context";

type FieldErrorMap = Record<string, ApiFieldError[]>;

export const FieldErrorProvider = ({ children }: { children: ReactNode }) => {
  const storeRef = useRef<FieldErrorMap>({});
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const setFieldErrors = useCallback(
    (formId: string, errors: ApiFieldError[]) => {
      storeRef.current[formId] = errors;
      bump();
    },
    [bump]
  );

  const clearForm = useCallback(
    (formId: string) => {
      if (formId in storeRef.current) {
        delete storeRef.current[formId];
        bump();
      }
    },
    [bump]
  );

  const clearField = useCallback(
    (formId: string, field: string) => {
      const current = storeRef.current[formId];
      if (!current || current.length === 0) return;
      const next = current.filter((e) => e.field !== field);
      if (next.length !== current.length) {
        storeRef.current[formId] = next;
        bump();
      }
    },
    [bump]
  );

  const getErrors = useCallback(
    (formId: string) => storeRef.current[formId] ?? [],
    []
  );

  const getFieldErrors = useCallback(
    (formId: string, field: string) => {
      const list = storeRef.current[formId];
      if (!list) return [];
      return list.filter((e) => e.field === field);
    },
    []
  );

  const value = useMemo<FieldErrorContextValue>(
    () => ({
      setFieldErrors,
      clearForm,
      clearField,
      getErrors,
      getFieldErrors,
      version,
    }),
    [setFieldErrors, clearForm, clearField, getErrors, getFieldErrors, version]
  );

  return <FieldErrorContext.Provider value={value}>{children}</FieldErrorContext.Provider>;
};
